import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const REDIRECT_DELAY = 3000; // 3 seconds

export const ReferralSuccess = ({ leadId, warning, onboardUrl, redirectUrl }) => {
  const [countdown, setCountdown] = useState(3);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Determine the final redirect URL (onboard_url takes priority)
  const finalRedirectUrl = onboardUrl || redirectUrl || 'https://kgob.com/thank-you';

  // Auto-redirect after countdown
  useEffect(() => {
    if (onboardUrl) {
      setIsRedirecting(true);
      
      // Countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Redirect after delay
      const redirectTimeout = setTimeout(() => {
        window.location.href = onboardUrl;
      }, REDIRECT_DELAY);

      return () => {
        clearInterval(countdownInterval);
        clearTimeout(redirectTimeout);
      };
    }
  }, [onboardUrl]);

  const handleContinue = () => {
    window.location.href = finalRedirectUrl;
  };

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="text-center py-8"
      data-testid="referral-success-container"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
        className="mx-auto w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mb-6"
      >
        <CheckCircle2 className="h-10 w-10 text-[#D4AF37]" />
      </motion.div>

      <h2 
        className="font-['Playfair_Display'] text-3xl sm:text-4xl text-white mb-4"
        data-testid="success-headline"
      >
        Thank You!
      </h2>
      
      {/* Show redirecting message if onboard_url is present */}
      {isRedirecting ? (
        <div className="mb-6">
          <p 
            className="text-white/80 text-lg mb-2 max-w-md mx-auto flex items-center justify-center gap-2"
            data-testid="success-redirecting-message"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
            Redirecting you to complete your profile...
          </p>
          <p className="text-[#E3C96E] text-sm">
            ({countdown} second{countdown !== 1 ? 's' : ''})
          </p>
        </div>
      ) : (
        <p 
          className="text-white/80 text-lg mb-2 max-w-md mx-auto"
          data-testid="success-message"
        >
          A KGOB representative will contact you shortly.
        </p>
      )}

      {leadId && (
        <p 
          className="text-white/50 text-sm mb-6"
          data-testid="success-lead-id"
        >
          Reference: {leadId}
        </p>
      )}

      {warning && (
        <div 
          className="mb-6 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg text-[#E3C96E] text-sm max-w-md mx-auto"
          data-testid="success-warning"
        >
          {warning}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={handleContinue}
          className="h-12 px-8 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-[#1FA3A3]"
          data-testid="success-continue-btn"
        >
          <span>{isRedirecting ? 'Continue Now' : 'Continue to KGOB'}</span>
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </motion.div>
  );
};

export default ReferralSuccess;
